import { Controller } from "@hotwired/stimulus"

/**
 * Executions List Controller
 * 
 * Handles the executions tab with:
 * - Status filtering (all, success, failure)
 * - n8n-style slide-over panel for execution details
 * - Agent runs integration
 */
export default class extends Controller {
  static targets = [
    "list", 
    "filters", 
    "slideOver", 
    "slideOverContent",
    "slideOverTitle",
    "slideOverStatus",
    "slideOverTime",
    "slideOverDuration",
    "slideOverTrigger",
    "slideOverResult",
    "slideOverInput",
    "slideOverError",
    "slideOverAgentSection",
    "slideOverTokens",
    "slideOverMessages",
    "slideOverToolCalls",
    "slideOverFinalResponse",
    "viewFullLink"
  ]
  
  static values = {
    outputsUrl: String,
    agentRunsUrl: String
  }

  connect() {
    console.log('[ExecutionsList] Connected')
    this.currentFilter = 'all'
    this.selectedExecution = null
  }

  // Filter executions by status
  filter(event) {
    const status = event.currentTarget.dataset.status
    this.currentFilter = status
    
    // Update filter button styles
    this.filtersTarget.querySelectorAll('button').forEach(btn => {
      const isActive = btn.dataset.status === status
      btn.classList.toggle('bg-gray-700', isActive)
      btn.classList.toggle('text-white', isActive)
      btn.classList.toggle('text-gray-400', !isActive)
    })
    
    // Filter rows
    this.listTarget.querySelectorAll('[data-execution-row]').forEach(row => {
      const rowStatus = row.dataset.status
      const visible = status === 'all' || rowStatus === status
      row.classList.toggle('hidden', !visible)
    })
  }

  // Open slide-over panel with execution details
  openSlideOver(event) {
    const row = event.currentTarget
    const executionData = JSON.parse(row.dataset.executionData)
    
    console.log('[ExecutionsList] Opening slide-over:', executionData)
    
    this.selectedExecution = executionData
    this.renderSlideOver(executionData)
    
    // Show slide-over
    this.slideOverTarget.classList.remove('translate-x-full')
    this.slideOverTarget.classList.add('translate-x-0')
    
    // Mark row as selected
    this.listTarget.querySelectorAll('[data-execution-row]').forEach(r => {
      r.classList.toggle('bg-purple-900/20', r === row)
      r.classList.toggle('border-l-2', r === row)
      r.classList.toggle('border-l-purple-500', r === row)
    })
  }

  // Close slide-over panel
  closeSlideOver() {
    this.slideOverTarget.classList.remove('translate-x-0')
    this.slideOverTarget.classList.add('translate-x-full')
    this.selectedExecution = null
    
    // Clear row selection
    this.listTarget.querySelectorAll('[data-execution-row]').forEach(r => {
      r.classList.remove('bg-purple-900/20', 'border-l-2', 'border-l-purple-500')
    })
  }

  // Render execution details in slide-over
  renderSlideOver(data) {
    const isAgentRun = data.type === 'agent_run'
    
    // Title
    this.slideOverTitleTarget.textContent = isAgentRun ? 'Agent Run' : 'Execution'
    
    // Status badge
    this.slideOverStatusTarget.innerHTML = this.renderStatusBadge(data.status)
    
    // Time
    this.slideOverTimeTarget.textContent = data.created_at
    
    // Duration
    this.slideOverDurationTarget.textContent = data.duration || '—'
    
    // Trigger
    this.slideOverTriggerTarget.textContent = data.trigger_name || 'Manual / Chain'
    
    // View full link
    if (this.hasViewFullLinkTarget) {
      this.viewFullLinkTarget.href = data.url
    }
    
    // Error (if any)
    if (data.error_message) {
      this.slideOverErrorTarget.classList.remove('hidden')
      this.slideOverErrorTarget.querySelector('[data-error-text]').textContent = data.error_message
    } else {
      this.slideOverErrorTarget.classList.add('hidden')
    }
    
    // Result
    if (data.result) {
      this.slideOverResultTarget.innerHTML = `<pre class="text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap">${this.escapeHtml(this.formatJson(data.result))}</pre>`
    } else {
      this.slideOverResultTarget.innerHTML = '<p class="text-gray-500 text-sm italic">No result data</p>'
    }
    
    // Input
    if (data.input_payload) {
      this.slideOverInputTarget.innerHTML = `<pre class="text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap">${this.escapeHtml(this.formatJson(data.input_payload))}</pre>`
    } else {
      this.slideOverInputTarget.innerHTML = '<p class="text-gray-500 text-sm italic">No input data</p>'
    }
    
    // Agent-specific sections
    if (isAgentRun && this.hasSlideOverAgentSectionTarget) {
      this.slideOverAgentSectionTarget.classList.remove('hidden')
      
      // Tokens
      if (this.hasSlideOverTokensTarget) {
        this.slideOverTokensTarget.innerHTML = `
          <div class="flex items-center gap-4 text-sm">
            <span class="text-gray-400">Input: <span class="text-white font-mono">${data.input_tokens || 0}</span></span>
            <span class="text-gray-400">Output: <span class="text-white font-mono">${data.output_tokens || 0}</span></span>
            <span class="text-gray-400">Total: <span class="text-white font-mono">${data.total_tokens || 0}</span></span>
          </div>
        `
      }
      
      // Messages
      if (this.hasSlideOverMessagesTarget && data.messages?.length > 0) {
        this.slideOverMessagesTarget.innerHTML = data.messages.map(msg => this.renderMessage(msg)).join('')
      } else if (this.hasSlideOverMessagesTarget) {
        this.slideOverMessagesTarget.innerHTML = '<p class="text-gray-500 text-sm italic">No messages</p>'
      }
      
      // Tool calls
      if (this.hasSlideOverToolCallsTarget && data.tool_calls?.length > 0) {
        this.slideOverToolCallsTarget.innerHTML = data.tool_calls.map(tc => this.renderToolCall(tc)).join('')
      } else if (this.hasSlideOverToolCallsTarget) {
        this.slideOverToolCallsTarget.innerHTML = '<p class="text-gray-500 text-sm italic">No tool calls</p>'
      }
      
      // Final response
      if (this.hasSlideOverFinalResponseTarget && data.final_response) {
        this.slideOverFinalResponseTarget.innerHTML = `<pre class="text-sm text-gray-300 whitespace-pre-wrap">${this.escapeHtml(data.final_response)}</pre>`
      } else if (this.hasSlideOverFinalResponseTarget) {
        this.slideOverFinalResponseTarget.innerHTML = '<p class="text-gray-500 text-sm italic">No response yet</p>'
      }
    } else if (this.hasSlideOverAgentSectionTarget) {
      this.slideOverAgentSectionTarget.classList.add('hidden')
    }
  }

  renderStatusBadge(status) {
    const colors = {
      success: 'bg-emerald-900/50 text-emerald-400 border-emerald-700',
      completed: 'bg-emerald-900/50 text-emerald-400 border-emerald-700',
      failure: 'bg-red-900/50 text-red-400 border-red-700',
      failed: 'bg-red-900/50 text-red-400 border-red-700',
      pending: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
      running: 'bg-blue-900/50 text-blue-400 border-blue-700',
      skipped: 'bg-gray-800 text-gray-400 border-gray-700',
      cancelled: 'bg-gray-800 text-gray-400 border-gray-700'
    }
    const colorClass = colors[status] || colors.skipped
    return `<span class="px-2 py-1 text-xs font-medium rounded border ${colorClass}">${this.capitalize(status)}</span>`
  }

  renderMessage(msg) {
    const isUser = msg.role === 'user'
    const bgClass = isUser ? 'bg-blue-900/30 border-blue-800' : 'bg-gray-800 border-gray-700'
    const roleLabel = isUser ? 'User' : 'Assistant'
    
    return `
      <div class="p-3 rounded-lg border ${bgClass} mb-2">
        <div class="text-xs text-gray-500 mb-1">${roleLabel}</div>
        <div class="text-sm text-gray-300 whitespace-pre-wrap">${this.escapeHtml(this.truncate(msg.content, 500))}</div>
      </div>
    `
  }

  renderToolCall(tc) {
    return `
      <div class="p-3 bg-amber-900/20 border border-amber-800 rounded-lg mb-2">
        <div class="flex items-center gap-2 mb-2">
          <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          <span class="text-sm font-medium text-amber-400">${this.escapeHtml(tc.name || tc.function?.name || 'Tool')}</span>
        </div>
        <pre class="text-xs text-gray-400 font-mono overflow-x-auto">${this.escapeHtml(this.formatJson(tc.input || tc.function?.arguments || {}))}</pre>
      </div>
    `
  }

  // Helpers
  formatJson(data) {
    if (typeof data === 'string') {
      try {
        return JSON.stringify(JSON.parse(data), null, 2)
      } catch {
        return data
      }
    }
    return JSON.stringify(data, null, 2)
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  truncate(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }

  capitalize(str) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
}
